import { state, api, $, $$, toast, escHtml } from './core';
import { _show3DUploadDialog } from './reports';
import { askConfirm } from './dialogs';

declare function openMachineDetails(machineId: string): void;

/* ═══ FACTORY LAYOUT (Hala / Layout) ══════════════════════════ */
export interface LayoutState {
    planId: number | null;
    plans: any[];
    equipment: any[];
    annotations: any[];
    photos: any[];
    dragging: any;
    panning: boolean;
    panStart: any;
    offset: { x: number; y: number };
    zoom: number;
    editMode: boolean;
    selectedEl: any;
    liveStatus?: any;
    allMachines?: any[];
    _canvasW?: number;
    _canvasH?: number;
}

export let _layoutState: LayoutState = { planId: null, plans: [], equipment: [], annotations: [], photos: [], dragging: null, panning: false, panStart: null, offset: { x: 0, y: 0 }, zoom: 1, editMode: false, selectedEl: null };

// Loading message version marker. Bump when changing the placeholder UI so the
// tag in screenshots / bug reports tells us which build is running.
const LAYOUT_LOADING_VERSION = 'ai72';

// Lazy-load helpers for Three.js + layout3d.js.
// Background (regression 2026-04-12, commit 0e8538a4):
//   The Three.js/OrbitControls/GLTFLoader/DRACOLoader/layout3d.js scripts used
//   to be hard-coded in templates/index.html and loaded eagerly at page load.
//   That was changed to a lazy `window._loadThreeJS()` to speed up overview,
//   but only `reconstruction.ts` was updated to call it. The Hală / Layout
//   tab kept calling `window.toggle3DLayout()` / `window.autoStart3D()` directly,
//   which silently no-op'd because neither function exists until layout3d.js is
//   downloaded. Result: clicking the "3D" toggle hid the 2D canvas without
//   ever showing the GLB model — black/empty viewport.
// Fix: lazily call `_loadThreeJS()` from this module when (a) the tab opens
// and a hall GLB model is available, and (b) when the user manually clicks
// the 3D button.
async function _ensureThreeJS(): Promise<boolean> {
    const w = window as any;
    if (w.THREE && typeof w.toggle3DLayout === 'function') return true;
    if (typeof w._loadThreeJS !== 'function') {
        console.warn('_loadThreeJS missing — three.js loader not bootstrapped in index.html');
        return false;
    }
    try {
        await w._loadThreeJS();
        return !!w.THREE && typeof w.toggle3DLayout === 'function';
    } catch (e) {
        console.error('three.js lazy-load failed:', e);
        return false;
    }
}

// Returns true if a "hall" GLB model is uploaded and converted, so the tab
// can auto-render in 3D the way it did before the lazy-load regression.
async function _hasHallGLBModel(): Promise<boolean> {
    try {
        const models = await api('/api/3d-models');
        return Array.isArray(models) && models.some((m: any) => m && m.model_type === 'hall' && m.conversion_status === 'done');
    } catch (e) {
        console.warn('hall model probe failed:', e);
        return false;
    }
}

// Render a fail-safe error panel inside #layout-container with a "Reîncarcă"
// button so the user never has to refresh the whole page when the layout tab
// fails to initialize.
function _renderLayoutError(container: HTMLElement, title: string, detail: string): void {
    container.innerHTML = `
        <div style="padding:2rem;background:rgba(239,68,68,.06);border:1px solid rgba(239,68,68,.35);border-radius:12px;color:#fca5a5;font-size:.95rem;line-height:1.5">
            <div style="font-size:1.05rem;font-weight:700;color:#f87171;margin-bottom:.6rem">${escHtml(title)}</div>
            <pre style="white-space:pre-wrap;margin:0 0 1rem;color:#fbbf24;font-size:.8rem;max-height:200px;overflow:auto">${escHtml(detail)}</pre>
            <div style="display:flex;gap:.6rem;flex-wrap:wrap">
                <button type="button" id="layout-retry-btn" class="btn btn-sm" style="background:#3b82f6;color:#fff;font-weight:700">↻ Reîncarcă layout</button>
                <button type="button" id="layout-reload-page-btn" class="btn btn-sm" style="background:rgba(255,255,255,.08);color:#e2e8f0">Reîncarcă întreaga pagină</button>
            </div>
        </div>`;
    document.getElementById('layout-retry-btn')?.addEventListener('click', () => { loadFactoryLayout(); });
    document.getElementById('layout-reload-page-btn')?.addEventListener('click', () => { window.location.reload(); });
}

export async function loadFactoryLayout(): Promise<void> {
    const container = document.getElementById('layout-container') as HTMLElement | null;
    if (!container) { console.warn('loadFactoryLayout: #layout-container missing'); return; }
    container.innerHTML = `<div style="padding:2rem;color:#fbbf24;font-size:1.1rem;font-weight:700;display:flex;align-items:center;gap:.8rem">
            <span class="layout-loading-spinner" style="width:24px;height:24px;border:3px solid rgba(251,191,36,.25);border-top-color:#fbbf24;border-radius:50%;display:inline-block;animation:layoutSpin 1s linear infinite"></span>
            <span>Layout se încarcă… <span style="color:#94a3b8;font-size:.8rem;font-weight:normal">(v=${LAYOUT_LOADING_VERSION})</span></span>
        </div>
        <style>@keyframes layoutSpin{to{transform:rotate(360deg)}}</style>`;
    try {
        await _loadFactoryLayoutInner(container);
    } catch (err) {
        console.error('loadFactoryLayout error:', err);
        const ex = err as any;
        _renderLayoutError(container, 'Eroare la încărcarea layout-ului', String(ex?.stack || ex?.message || err));
    }
}

// Race the given promise against a hard timeout (ms). Rejects with a
// descriptive Error on timeout so the caller can show a clean error UI
// instead of leaving the loading placeholder forever.
function _withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        let done = false;
        const t = setTimeout(() => {
            if (done) return;
            done = true;
            reject(new Error(`Timeout (${ms} ms) la ${label}`));
        }, ms);
        p.then(v => { if (done) return; done = true; clearTimeout(t); resolve(v); },
               e => { if (done) return; done = true; clearTimeout(t); reject(e); });
    });
}

async function _loadFactoryLayoutInner(container: HTMLElement): Promise<void> {
    const isMaster = !!(state.currentUser && state.currentUser.role === 'master');

    // 15 s hard cap so a single slow/hung endpoint can never leave the UI
    // stuck on the loading placeholder. Each individual fetch keeps its own
    // .catch() fallback (empty list / object) — the timeout is a safety net.
    const [plans, photos, machines, liveStatus] = await _withTimeout(Promise.all([
        api('/api/layout/plans').catch((e: any) => { console.warn('layout/plans:', e); return []; }),
        api('/api/layout/photos').catch((e: any) => { console.warn('layout/photos:', e); return []; }),
        api('/api/machines/all').catch((e: any) => { console.warn('machines/all:', e); return []; }),
        api('/api/layout/live_status').catch((e: any) => { console.warn('layout/live_status:', e); return {}; }),
    ]), 15000, 'încărcare date layout');
    _layoutState.plans = plans || [];
    _layoutState.photos = photos || [];
    _layoutState.liveStatus = liveStatus;
    _layoutState.allMachines = machines || [];

    const activePlan = (plans || []).find((p: any) => p.is_active) || (plans || [])[0];

    container.innerHTML = `
        <div class="layout-toolbar" style="background:var(--card-bg,#1e293b);padding:8px 12px;border:1px solid var(--border);border-radius:8px;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px">
            <div class="layout-toolbar-left" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                <span style="font-size:.9rem;color:#e2e8f0;font-weight:700">LAYOUT FABRICĂ</span>
                <select id="layout-plan-select" class="form-control" style="max-width:250px;font-size:.8rem;display:none">
                    <option value="">— Plan activ —</option>
                    ${(plans||[]).map((p: any) => `<option value="${p.id}" ${activePlan && p.id === activePlan.id ? 'selected' : ''}>${escHtml(p.name)}</option>`).join('')}
                </select>
                ${isMaster ? `<button class="btn btn-sm btn-green" id="layout-upload-btn" title="Încarcă planuri noi">📤</button>
                <button class="btn btn-sm" id="layout-photo-btn" title="Încarcă poze hală">📷</button>
                <button class="btn btn-sm" id="layout-3d-upload-btn" title="Încarcă modele 3D (STEP/IGES)" style="background:linear-gradient(135deg,#8b5cf6,#6366f1);color:#fff">🧊 Model 3D</button>
                <button class="btn btn-sm" id="layout-manage-plans-btn" title="Planuri tehnice (${(plans||[]).length})">📋 Planuri (${(plans||[]).length})</button>
                <button class="btn btn-sm btn-accent" id="layout-edit-toggle" title="Mod editare">✏️ Editare</button>` : ''}
            </div>
            <div class="layout-toolbar-right" style="display:flex;align-items:center;gap:6px">
                <div id="layout-2d-tools" style="display:flex;align-items:center;gap:4px">
                    <button class="btn btn-sm" id="layout-zoom-in" title="Zoom in" style="font-size:.75rem;padding:4px 8px">+</button>
                    <button class="btn btn-sm" id="layout-zoom-out" title="Zoom out" style="font-size:.75rem;padding:4px 8px">−</button>
                    <button class="btn btn-sm" id="layout-fit" title="Încadrează" style="font-size:.75rem;padding:4px 8px">⊞</button>
                    <span id="layout-zoom-label" style="font-size:.75rem;color:var(--text-muted)">100%</span>
                </div>
                <div style="display:flex;border-radius:8px;overflow:hidden;border:2px solid rgba(99,102,241,.5)">
                    <button class="btn btn-sm" id="layout-view-2d" style="background:#6366f1;color:#fff;font-weight:700;padding:6px 16px;font-size:.85rem;border-radius:0;border:none">2D</button>
                    <button class="btn btn-sm" id="layout-view-3d" style="background:transparent;color:#94a3b8;font-weight:700;padding:6px 16px;font-size:.85rem;border-radius:0;border:none">3D</button>
                </div>
            </div>
        </div>
        <div class="layout-canvas-wrap" id="layout-canvas-wrap" style="position:relative;overflow:hidden;border:1px solid var(--border);border-radius:8px;background:#0c0c1a;min-height:550px;cursor:grab">
            <div id="layout-canvas" style="position:absolute;top:0;left:0;transform-origin:0 0;transition:transform .1s">
                <div id="layout-floor" style="position:relative"></div>
                <div id="layout-elements" style="position:absolute;top:0;left:0;width:100%;height:100%"></div>
            </div>
        </div>
        <div id="layout-3d-container" style="display:none;width:100%;height:70vh;min-height:500px;border-radius:8px;overflow:hidden;position:relative;background:linear-gradient(180deg,#0f172a,#1e293b)">
            <div id="layout-3d-controls" style="position:absolute;top:10px;right:10px;z-index:10;display:flex;flex-direction:column;gap:3px">
                <select id="camera-preset-select" onchange="setCameraPreset(this.value)" style="background:rgba(15,23,42,.85);color:#e2e8f0;border:1px solid rgba(255,255,255,.2);border-radius:6px;padding:5px 8px;font-size:.72rem;cursor:pointer;backdrop-filter:blur(4px)">
                    <option value="iso">Izometric</option>
                    <option value="top">De sus</option>
                    <option value="front">Frontal</option>
                    <option value="side">Lateral</option>
                    <option value="top2d">Plan 2D</option>
                </select>
            
            </div>
            
        </div>
        <div id="layout-photo-viewer" class="hidden" style="position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.85);z-index:9999;display:flex;align-items:center;justify-content:center;cursor:pointer">
            <img id="layout-photo-viewer-img" style="max-width:90%;max-height:90%;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,.5)">
            <span style="position:absolute;top:20px;right:30px;color:#fff;font-size:2rem;cursor:pointer" onclick="document.getElementById('layout-photo-viewer').classList.add('hidden')">✕</span>
        </div>
        ${isMaster ? `<div id="layout-manage-panel" class="layout-form hidden" style="margin-top:1rem;padding:1rem;border:1px solid var(--border);border-radius:8px;background:var(--card-bg);max-height:300px;overflow-y:auto">
            <h4 style="margin:0 0 .5rem">📋 Planuri încărcate (${(plans||[]).length})</h4>
            <table style="width:100%;font-size:.85rem;border-collapse:collapse">
                <thead><tr style="text-align:left;border-bottom:1px solid var(--border)"><th style="padding:4px">#</th><th style="padding:4px">Nume</th><th style="padding:4px">Tip</th><th style="padding:4px">Fișier</th><th style="padding:4px">Activ</th><th style="padding:4px">Acțiuni</th></tr></thead>
                <tbody>${(plans||[]).map((p: any, i: number) => `<tr style="border-bottom:1px solid var(--border-light,#333)">
                    <td style="padding:4px">${i+1}</td>
                    <td style="padding:4px">${escHtml(p.name)}</td>
                    <td style="padding:4px">${escHtml(p.plan_type||'layout')}</td>
                    <td style="padding:4px;font-size:.75rem;color:var(--text-muted)">${escHtml(p.original_name||p.filename||'')}</td>
                    <td style="padding:4px">${p.is_active ? '★' : ''}</td>
                    <td style="padding:4px;white-space:nowrap">
                        <button class="btn btn-sm" onclick="(async()=>{await fetch('/api/layout/plans/${p.id}/activate',{method:'POST',credentials:'include'});loadFactoryLayout()})()">Activează</button>
                        <button class="btn btn-sm btn-red layout-plan-del" data-plan-id="${p.id}" data-plan-name="${escHtml(p.name)}">🗑</button>
                    </td>
                </tr>`).join('')}</tbody>
            </table>
        </div>
        <div id="layout-upload-form" class="layout-form hidden" style="margin-top:1rem;padding:1rem;border:1px solid var(--border);border-radius:8px;background:var(--card-bg)">
            <h4 style="margin:0 0 .5rem">📤 Încarcă planuri hală</h4>
            <form id="layout-plan-form" enctype="multipart/form-data" style="display:flex;flex-wrap:wrap;gap:.5rem;align-items:end">
                <label>Tip:<select name="plan_type" class="form-control"><option value="layout">Layout</option><option value="structural">Structură rezistență</option><option value="electrical">Electrică</option><option value="pneumatic">Pneumatică</option><option value="hydraulic">Hidraulică</option><option value="ventilation">Ventilație</option><option value="fire">Incendiu</option><option value="photo">Poză</option></select></label>
                <label>Fișiere (multiple):<input type="file" name="files" accept=".pdf,.png,.jpg,.jpeg,.svg,.dwg,.dxf" multiple required></label>
                <div id="layout-upload-count" style="font-size:.8rem;color:var(--text-muted)"></div>
                <button type="submit" class="btn btn-green">Încarcă</button>
            </form>
        </div>
        <div id="layout-photo-form" class="layout-form hidden" style="margin-top:1rem;padding:1rem;border:1px solid var(--border);border-radius:8px;background:var(--card-bg)">
            <h4 style="margin:0 0 .5rem">📷 Încarcă poze hală</h4>
            <form id="layout-photos-form" enctype="multipart/form-data" style="display:flex;flex-wrap:wrap;gap:.5rem;align-items:end">
                <label>Tip:<select name="photo_type" class="form-control"><option value="interior">Interior</option><option value="exterior">Exterior</option><option value="detail">Detaliu</option><option value="equipment">Utilaje</option><option value="panel">Panouri</option></select></label>
                <label>Fișiere (multiple):<input type="file" name="files" accept="image/*" multiple required></label>
                <button type="submit" class="btn btn-green">Încarcă</button>
            </form>
            <div id="layout-photos-gallery" style="display:flex;flex-wrap:wrap;gap:8px;margin-top:.5rem"></div>
        </div>
        <div id="layout-edit-panel" class="hidden" style="margin-top:1rem;padding:1rem;border:1px solid var(--accent);border-radius:8px;background:var(--card-bg)">
            <h4 style="margin:0 0 .5rem">🛠 Mod Editare — glisează elementele pe plan</h4>
            <div style="display:flex;flex-wrap:wrap;gap:.3rem .5rem;margin-bottom:.5rem;align-items:end">
                <fieldset style="border:1px solid var(--border);border-radius:6px;padding:.4rem .6rem;margin:0">
                    <legend style="font-size:.75rem;color:var(--text-muted);padding:0 4px">Utilaje</legend>
                    <div style="display:flex;gap:.3rem;align-items:center">
                        <select id="layout-add-machine" class="form-control" style="max-width:220px;font-size:.85rem"><option value="">+ Utilaj</option></select>
                        <button class="btn btn-sm btn-green" id="layout-add-machine-btn">+</button>
                    </div>
                </fieldset>
                <fieldset style="border:1px solid var(--border);border-radius:6px;padding:.4rem .6rem;margin:0">
                    <legend style="font-size:.75rem;color:var(--text-muted);padding:0 4px">Zone</legend>
                    <div style="display:flex;gap:.3rem;align-items:center">
                        <select id="layout-add-zone-type" class="form-control" style="max-width:140px;font-size:.85rem">
                            <option value="production">Producție</option>
                            <option value="warehouse">Depozit</option>
                            <option value="utility">Utilități</option>
                            <option value="office">Birou</option>
                            <option value="corridor">Coridor</option>
                            <option value="wall">Perete/Panou</option>
                        </select>
                        <input id="layout-add-zone-label" class="form-control" style="width:100px;font-size:.85rem" placeholder="Nume zonă">
                        <button class="btn btn-sm" id="layout-add-zone-btn" style="background:#8b5cf6;color:#fff">+ Zonă</button>
                    </div>
                </fieldset>
                <fieldset style="border:1px solid var(--border);border-radius:6px;padding:.4rem .6rem;margin:0">
                    <legend style="font-size:.75rem;color:var(--text-muted);padding:0 4px">Puncte</legend>
                    <div style="display:flex;gap:.3rem;align-items:center">
                        <select id="layout-add-annot-type" class="form-control" style="max-width:120px;font-size:.85rem">
                            <option value="sensor">Senzor</option>
                            <option value="valve">Robinet</option>
                            <option value="pipe">Conductă</option>
                            <option value="panel">Panou electric</option>
                            <option value="door">Ușă</option>
                            <option value="fire">Stingător</option>
                            <option value="label">Etichetă</option>
                        </select>
                        <input id="layout-add-annot-label" class="form-control" style="width:100px;font-size:.85rem" placeholder="Label">
                        <button class="btn btn-sm" id="layout-add-annot-btn">+ Punct</button>
                    </div>
                </fieldset>
                <div style="margin-left:auto;display:flex;gap:.3rem">
                    <button class="btn btn-sm btn-green" id="layout-save-all">💾 Salvează</button>
                    <button class="btn btn-sm btn-red" id="layout-delete-selected" disabled>🗑 Șterge</button>
                </div>
            </div>
            <div id="layout-props-panel" style="display:none;padding:.5rem;border:1px solid var(--border);border-radius:6px;background:rgba(0,0,0,.15);margin-top:.3rem">
                <span style="font-size:.8rem;font-weight:600" id="layout-props-title">Proprietăți</span>
                <div style="display:flex;flex-wrap:wrap;gap:.4rem;margin-top:.3rem;align-items:center" id="layout-props-fields"></div>
            </div>
            <p style="font-size:.75rem;color:var(--text-muted);margin:.3rem 0 0">Click = selectare · Drag = mutare · Scroll = zoom · Drag colț = redimensionare</p>
        </div>` : ''}
        <div id="layout-photos-section" style="margin-top:1rem">
            ${(photos||[]).length ? `<h4 style="display:flex;align-items:center;gap:8px">📷 Galerie poze hală <span id="layout-photos-count" style="font-size:.8rem;color:var(--text-muted);font-weight:normal">(${photos.length} poze)</span></h4>
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:6px;max-height:280px;overflow-y:auto;padding:4px;border-radius:8px;background:rgba(0,0,0,.15)">` + (photos||[]).map((p: any) => `<div class="layout-photo-thumb" style="position:relative;border-radius:6px;overflow:hidden;aspect-ratio:3/2;cursor:pointer" onclick="_layoutViewPhoto('/uploads/layout/${p.filename}')">
                <img src="/uploads/layout/${p.filename}" loading="lazy" style="width:100%;height:100%;object-fit:cover;transition:transform .3s" onmouseenter="this.style.transform='scale(1.05)'" onmouseleave="this.style.transform='scale(1)'">
                <div style="position:absolute;bottom:0;left:0;right:0;padding:2px 4px;background:linear-gradient(transparent,rgba(0,0,0,.7));font-size:.65rem;color:#ccc">${escHtml(p.photo_type||'')} — ${escHtml((p.description||'').substring(0,30))}</div>
                ${isMaster ? `<button class="btn btn-sm btn-red" style="position:absolute;top:2px;right:2px;padding:0 4px;font-size:.65rem;opacity:.7" onclick="event.stopPropagation();_layoutDeletePhoto(${p.id})">✕</button>` : ''}
            </div>`).join('') + '</div>' : ''}
        </div>
    `;

    // From here on, any throw should NOT bubble up to the outer catch (we
    // already replaced the loading message with the real UI). A failure to
    // wire one event listener must not blank the whole layout.
    try { _layoutBindEvents(isMaster); }
    catch (e) { console.error('_layoutBindEvents failed:', e); }

    if (activePlan) {
        try { _layoutLoadPlan(activePlan.id); }
        catch (e) { console.error('_layoutLoadPlan failed:', e); }
    }

    // Auto-render 3D when entering the tab IF a hall GLB exists.
    // This restores the pre-2026-04-12 behavior: open Hală / Layout → see the
    // factory-hall GLB rendered in the canvas without an extra click. The
    // download of three.js (~600 kB) is gated on the presence of a hall model
    // so users without uploaded 3D content do not pay the bandwidth cost.
    setTimeout(() => { _autoStart3DIfAvailable().catch(e => console.warn('autoStart3D failed:', e)); }, 100);
}

async function _autoStart3DIfAvailable(): Promise<void> {
    const c3d = document.getElementById('layout-3d-container');
    if (!c3d) return;
    if (!(await _hasHallGLBModel())) return;
    if (!(await _ensureThreeJS())) return;
    const w = window as any;
    // Already showing 3D? avoid double-toggle (re-enter from edit/save).
    const already3D = c3d.style.display !== 'none';
    if (!already3D && typeof w.toggle3DLayout === 'function') {
        w.toggle3DLayout();
    }
    const canvas2d = document.getElementById('layout-canvas-wrap');
    if (canvas2d) canvas2d.style.display = 'none';
    if (typeof w.setCameraPreset === 'function') {
        try { w.setCameraPreset('iso'); } catch (_) { /* ignore */ }
    }
    const sel = document.getElementById('camera-preset-select') as HTMLSelectElement | null;
    if (sel) { sel.style.display = ''; sel.value = 'iso'; }
    const btn2d = document.getElementById('layout-view-2d') as HTMLElement | null;
    const btn3d = document.getElementById('layout-view-3d') as HTMLElement | null;
    if (btn2d) { btn2d.style.background = 'transparent'; btn2d.style.color = '#94a3b8'; }
    if (btn3d) { btn3d.style.background = '#6366f1'; btn3d.style.color = '#fff'; }
    const tools2d = document.getElementById('layout-2d-tools');
    if (tools2d) tools2d.style.display = 'none';
}

function _layoutBindEvents(isMaster: boolean): void {
    document.getElementById('layout-plan-select')?.addEventListener('change', e => {
        const t = e.target as HTMLSelectElement;
        if (t.value) _layoutLoadPlan(parseInt(t.value, 10));
    });
    // Ștergerea planului: era `onclick="if(confirm(...))"` inline în HTML —
    // ultimul confirm() nativ din aplicație (audit UX, 8 aug 2026).
    document.querySelectorAll('.layout-plan-del').forEach(btn => {
        btn.addEventListener('click', async () => {
            const el = btn as HTMLElement;
            const id = el.dataset.planId;
            if (!id) return;
            if (!await askConfirm('Ștergi planul?', el.dataset.planName || '', 'Șterge')) return;
            await fetch(`/api/layout/plans/${id}`, { method: 'DELETE', credentials: 'include' });
            loadFactoryLayout();
        });
    });
    document.getElementById('layout-zoom-in')?.addEventListener('click', () => _layoutZoom(0.15));
    document.getElementById('layout-zoom-out')?.addEventListener('click', () => _layoutZoom(-0.15));
    document.getElementById('layout-fit')?.addEventListener('click', () => { _layoutState.zoom = 1; _layoutState.offset = {x:0,y:0}; _layoutApplyTransform(); });

    async function _setViewMode(mode3d: boolean): Promise<void> {
        const btn2d = document.getElementById('layout-view-2d');
        const btn3d = document.getElementById('layout-view-3d');
        const tools2d = document.getElementById('layout-2d-tools');
        const c3d = document.getElementById('layout-3d-container');
        const canvas2d = document.getElementById('layout-canvas-wrap');

        if (mode3d) {
            // Switch to 3D: ensure three.js + layout3d.js are downloaded BEFORE
            // we toggle anything visible (regression 2026-04-12 fix). Without
            // this, toggle3DLayout was silently undefined → 2D got hidden but
            // 3D never showed up → black canvas.
            const ok = await _ensureThreeJS();
            if (!ok) {
                toast('Nu am putut încărca biblioteca 3D (three.js). Verifică conexiunea internet.', 'error');
                return;
            }
            const w = window as any;
            const is3DVisible = c3d && c3d.style.display !== 'none';
            if (!is3DVisible && typeof w.toggle3DLayout === 'function') {
                w.toggle3DLayout();
            }
            if (canvas2d) canvas2d.style.display = 'none';
            if (typeof w.setCameraPreset === 'function') w.setCameraPreset('iso');
            const sel3d = document.getElementById('camera-preset-select') as HTMLSelectElement | null;
            if (sel3d) { sel3d.style.display = ''; sel3d.value = 'iso'; }
        } else {
            // Switch to 2D: hide the 3D viewport (if any), bring back the 2D
            // canvas. Do NOT trigger three.js download just to switch off 3D.
            const w = window as any;
            if (c3d && c3d.style.display !== 'none' && typeof w.forceFactoryLayout2D === 'function') {
                w.forceFactoryLayout2D();
            } else {
                if (c3d) c3d.style.display = 'none';
                if (canvas2d) canvas2d.style.display = '';
            }
            const sel2d = document.getElementById('camera-preset-select') as HTMLSelectElement | null;
            if (sel2d) sel2d.style.display = 'none';
        }

        if (btn2d) { btn2d.style.background = mode3d ? 'transparent' : '#6366f1'; btn2d.style.color = mode3d ? '#94a3b8' : '#fff'; }
        if (btn3d) { btn3d.style.background = mode3d ? '#6366f1' : 'transparent'; btn3d.style.color = mode3d ? '#fff' : '#94a3b8'; }
        if (tools2d) tools2d.style.display = mode3d ? 'none' : 'flex';
    }
    document.getElementById('layout-view-2d')?.addEventListener('click', () => { void _setViewMode(false); });
    document.getElementById('layout-view-3d')?.addEventListener('click', () => { void _setViewMode(true); });

    if (!isMaster) return;

    document.getElementById('layout-upload-btn')?.addEventListener('click', () => {
        document.getElementById('layout-upload-form')?.classList.toggle('hidden');
        document.getElementById('layout-photo-form')?.classList.add('hidden');
        document.getElementById('layout-manage-panel')?.classList.add('hidden');
    });
    document.getElementById('layout-manage-plans-btn')?.addEventListener('click', () => {
        document.getElementById('layout-manage-panel')?.classList.toggle('hidden');
        document.getElementById('layout-upload-form')?.classList.add('hidden');
        document.getElementById('layout-photo-form')?.classList.add('hidden');
    });
    document.getElementById('layout-photo-btn')?.addEventListener('click', () => {
        document.getElementById('layout-photo-form')?.classList.toggle('hidden');
        document.getElementById('layout-upload-form')?.classList.add('hidden');
        document.getElementById('layout-manage-panel')?.classList.add('hidden');
    });
    document.getElementById('layout-3d-upload-btn')?.addEventListener('click', () => {
        _show3DUploadDialog();
    });
    document.getElementById('layout-edit-toggle')?.addEventListener('click', () => {
        _layoutState.editMode = !_layoutState.editMode;
        const btn = document.getElementById('layout-edit-toggle') as HTMLButtonElement;
        btn.textContent = _layoutState.editMode ? '✅ Gata editare' : '✏️ Editare';
        btn.classList.toggle('btn-accent', !_layoutState.editMode);
        btn.classList.toggle('btn-green', _layoutState.editMode);
        document.getElementById('layout-edit-panel')?.classList.toggle('hidden', !_layoutState.editMode);
        const wrap = document.getElementById('layout-canvas-wrap');
        if (wrap) { wrap.style.cursor = _layoutState.editMode ? 'default' : 'grab'; wrap.classList.toggle('edit-mode', _layoutState.editMode); }
        (document.getElementById('layout-plan-select') as HTMLSelectElement).style.display = _layoutState.editMode ? '' : 'none';
        _layoutRenderElements();
    });

    document.getElementById('layout-plan-form')?.querySelector('input[name="files"]')?.addEventListener('change', e => {
        const cnt = (e.target as HTMLInputElement).files?.length || 0;
        const el = document.getElementById('layout-upload-count');
        if (el) el.textContent = cnt > 0 ? `${cnt} fișiere selectate` : '';
    });
    document.getElementById('layout-plan-form')?.addEventListener('submit', async e => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const files = (form.querySelector('input[name="files"]') as HTMLInputElement).files;
        const planType = (form.querySelector('select[name="plan_type"]') as HTMLSelectElement).value;
        if (!files || files.length === 0) { toast('Selectează fișiere', 'error'); return; }
        const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
        const origText = btn.textContent;
        btn.disabled = true;
        let ok = 0, fail = 0;
        for (let i = 0; i < files.length; i++) {
            btn.textContent = `Încărcare ${i+1}/${files.length}...`;
            const fd = new FormData();
            fd.append('file', files[i]);
            fd.append('name', files[i].name.replace(/\.[^.]+$/, '').replace(/_/g, ' '));
            fd.append('plan_type', planType);
            try {
                const res = await fetch('/api/layout/plans', { method: 'POST', body: fd, credentials: 'include' });
                const data = await res.json();
                if (data.ok) ok++; else fail++;
            } catch { fail++; }
        }
        btn.disabled = false;
        btn.textContent = origText;
        toast(`Încărcat: ${ok} planuri${fail ? `, ${fail} erori` : ''}`, fail ? 'warning' : 'success');
        loadFactoryLayout();
    });
    document.getElementById('layout-photos-form')?.addEventListener('submit', async e => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const files = (form.querySelector('input[name="files"]') as HTMLInputElement).files;
        const photoType = (form.querySelector('select[name="photo_type"]') as HTMLSelectElement).value;
        if (!files || files.length === 0) { toast('Selectează poze', 'error'); return; }
        const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
        btn.disabled = true;
        let ok = 0;
        for (let i = 0; i < files.length; i++) {
            btn.textContent = `${i+1}/${files.length}...`;
            const fd = new FormData();
            fd.append('file', files[i]);
            fd.append('photo_type', photoType);
            fd.append('description', files[i].name.replace(/\.[^.]+$/, ''));
            try {
                const res = await fetch('/api/layout/photos', { method: 'POST', body: fd, credentials: 'include' });
                const data = await res.json();
                if (data.ok) ok++;
            } catch {}
        }
        btn.disabled = false;
        btn.textContent = 'Încarcă';
        toast(`${ok} poze încărcate`);
        loadFactoryLayout();
    });

    document.getElementById('layout-add-machine-btn')?.addEventListener('click', () => {
        const sel = document.getElementById('layout-add-machine') as HTMLSelectElement | null;
        const mid = sel?.value;
        if (!mid) return;
        if (_layoutState.equipment.find(e => e.machine_id === mid)) { toast('Deja pe plan'); return; }
        const m = (_layoutState.allMachines || []).find((m: any) => m.id === mid);
        _layoutState.equipment.push({ machine_id: mid, x_pct: 45+Math.random()*10, y_pct: 45+Math.random()*10, width_pct: 6, height_pct: 4, rotation: 0, label: m?.name || mid, color: '#3b82f6', shape: 'rect', z_index: 1 });
        _layoutRenderElements();
    });

    document.getElementById('layout-add-annot-btn')?.addEventListener('click', () => {
        const atype = (document.getElementById('layout-add-annot-type') as HTMLSelectElement | null)?.value || 'sensor';
        const label = (document.getElementById('layout-add-annot-label') as HTMLInputElement | null)?.value || atype;
        const icons: Record<string, string> = { sensor: '📡', valve: '🔧', pipe: '🔵', panel: '⚡', door: '🚪', fire: '🧯', label: '🏷' };
        const colors: Record<string, string> = { sensor: '#f59e0b', valve: '#ef4444', pipe: '#3b82f6', panel: '#22c55e', door: '#8b5cf6', fire: '#ff6b6b', label: '#a855f7' };
        _layoutState.annotations.push({ annotation_type: atype, label, x_pct: 50+Math.random()*5, y_pct: 50+Math.random()*5, icon: icons[atype]||'📌', color: colors[atype]||'#f59e0b', machine_id: '', metadata: '' });
        _layoutRenderElements();
    });

    document.getElementById('layout-add-zone-btn')?.addEventListener('click', () => {
        const ztype = (document.getElementById('layout-add-zone-type') as HTMLSelectElement | null)?.value || 'production';
        const label = (document.getElementById('layout-add-zone-label') as HTMLInputElement | null)?.value || '';
        const zoneColors: Record<string, string> = { production: '#3b82f6', warehouse: '#f59e0b', utility: '#22c55e', office: '#a855f7', corridor: '#6b7280', wall: '#94a3b8' };
        const zoneLabels: Record<string, string> = { production: 'Zona producție', warehouse: 'Depozit', utility: 'Utilități', office: 'Birou', corridor: 'Coridor', wall: 'Perete' };
        const isWall = ztype === 'wall';
        _layoutState.equipment.push({
            machine_id: '_zone_' + Date.now(),
            x_pct: 20 + Math.random()*20,
            y_pct: 20 + Math.random()*20,
            width_pct: isWall ? 15 : 20,
            height_pct: isWall ? 0.5 : 15,
            rotation: 0,
            label: label || zoneLabels[ztype] || ztype,
            color: zoneColors[ztype] || '#6b7280',
            shape: isWall ? 'wall' : 'zone',
            z_index: isWall ? 0 : -1,
        });
        _layoutRenderElements();
    });

    document.getElementById('layout-save-all')?.addEventListener('click', async () => {
        if (!_layoutState.planId) { toast('Niciun plan selectat','error'); return; }
        await Promise.all([
            fetch(`/api/layout/equipment/${_layoutState.planId}`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ positions: _layoutState.equipment }) }),
            fetch(`/api/layout/annotations/${_layoutState.planId}`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ annotations: _layoutState.annotations }) })
        ]);
        toast('Poziții salvate! ✅');
    });

    document.getElementById('layout-delete-selected')?.addEventListener('click', () => {
        const sel = _layoutState.selectedEl;
        if (!sel) return;
        if (sel.type === 'equipment') _layoutState.equipment = _layoutState.equipment.filter(e => e.machine_id !== sel.id);
        if (sel.type === 'annotation') _layoutState.annotations = _layoutState.annotations.filter((_, i) => i !== sel.idx);
        _layoutState.selectedEl = null;
        (document.getElementById('layout-delete-selected') as HTMLButtonElement).disabled = true;
        _layoutRenderElements();
    });

    const wrap = document.getElementById('layout-canvas-wrap') as HTMLElement | null;
    wrap?.addEventListener('wheel', e => { e.preventDefault(); _layoutZoom(e.deltaY < 0 ? 0.1 : -0.1); });
    let panActive = false, panStartX: number, panStartY: number, ofsStartX: number, ofsStartY: number;
    wrap?.addEventListener('mousedown', e => {
        if (_layoutState.editMode && (e.target as HTMLElement).closest('.layout-el')) return;
        panActive = true; panStartX = e.clientX; panStartY = e.clientY;
        ofsStartX = _layoutState.offset.x; ofsStartY = _layoutState.offset.y;
        wrap!.style.cursor = 'grabbing';
    });
    window.addEventListener('mousemove', e => {
        if (!panActive) return;
        _layoutState.offset.x = ofsStartX + (e.clientX - panStartX);
        _layoutState.offset.y = ofsStartY + (e.clientY - panStartY);
        _layoutApplyTransform();
    });
    window.addEventListener('mouseup', () => { panActive = false; const w = document.getElementById('layout-canvas-wrap') as HTMLElement | null; if (w) w.style.cursor = _layoutState.editMode ? 'default' : 'grab'; });
}

async function _layoutLoadPlan(pid: number): Promise<void> {
    _layoutState.planId = pid;

    const canvas = document.getElementById('layout-canvas') as HTMLElement | null;
    const floor = document.getElementById('layout-floor') as HTMLElement | null;
    const els = document.getElementById('layout-elements') as HTMLElement | null;
    if (!canvas || !floor) return;

    const plan = (_layoutState.plans || []).find(p => p.id === pid);
    const hasImage = plan && /\.(jpe?g|png|gif|webp|svg)$/i.test(plan.filename || '');

    if (hasImage) {
        await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
                const ratio = img.naturalWidth / img.naturalHeight;
                const MAX_H = 800;
                const CANVAS_H = Math.min(img.naturalHeight * 2, MAX_H);
                const CANVAS_W = Math.round(CANVAS_H * ratio);
                canvas.style.width = CANVAS_W + 'px';
                canvas.style.height = CANVAS_H + 'px';
                if (els) { els.style.width = CANVAS_W + 'px'; els.style.height = CANVAS_H + 'px'; }
                floor.style.cssText = `width:${CANVAS_W}px;height:${CANVAS_H}px;position:relative;overflow:hidden`;
                floor.innerHTML = `<img src="/api/layout/plans/${pid}/file" style="width:100%;height:100%;object-fit:contain;pointer-events:none;display:block">`;
                _layoutState._canvasW = CANVAS_W;
                _layoutState._canvasH = CANVAS_H;
                _layoutState.zoom = 1; _layoutState.offset = {x:0,y:0};
                const wrap = document.getElementById('layout-canvas-wrap');
                if (wrap) {
                    const scale = Math.min(wrap.clientWidth / CANVAS_W, 650 / CANVAS_H, 1);
                    _layoutState.zoom = scale;
                }
                _layoutApplyTransform();
                resolve();
            };
            img.onerror = () => {
                _layoutFallbackFloor(canvas, floor, els);
                resolve();
            };
            img.src = `/api/layout/plans/${pid}/file`;
        });
    } else {
        _layoutFallbackFloor(canvas, floor, els);
    }

    const [equipment, annotations] = await Promise.all([
        api(`/api/layout/equipment/${pid}`).catch(() => []),
        api(`/api/layout/annotations/${pid}`).catch(() => []),
    ]);
    _layoutState.equipment = equipment || [];
    _layoutState.annotations = annotations || [];

    const addSel = document.getElementById('layout-add-machine');
    if (addSel) {
        const placed = new Set(_layoutState.equipment.map(e => e.machine_id));
        addSel.innerHTML = '<option value="">+ Adaugă utilaj pe plan</option>' +
            (_layoutState.allMachines || []).filter(m => !placed.has(m.id)).map(m =>
                `<option value="${m.id}">${m.id} — ${escHtml((m.name||'').substring(0,30))}</option>`
            ).join('');
    }
    _layoutRenderElements();
}

function _layoutFallbackFloor(canvas: HTMLElement, floor: HTMLElement, els: HTMLElement | null): void {
    const CANVAS_W = 1400, CANVAS_H = 700;
    canvas.style.width = CANVAS_W + 'px';
    canvas.style.height = CANVAS_H + 'px';
    if (els) { els.style.width = CANVAS_W + 'px'; els.style.height = CANVAS_H + 'px'; }
    _layoutDrawFloor(floor, CANVAS_W, CANVAS_H);
    _layoutState._canvasW = CANVAS_W;
    _layoutState._canvasH = CANVAS_H;
    _layoutState.zoom = 1; _layoutState.offset = {x:0,y:0};
    const wrap = document.getElementById('layout-canvas-wrap');
    if (wrap) {
        const scale = Math.min(wrap.clientWidth / CANVAS_W, 550 / CANVAS_H, 1);
        _layoutState.zoom = scale;
    }
    _layoutApplyTransform();
}

function _layoutDrawFloor(floor: HTMLElement, W: number, H: number): void {
    const MARGIN = 40;
    const HALL_X = MARGIN + 60;
    const HALL_Y = MARGIN + 20;
    const HALL_W = W - MARGIN * 2 - 120;
    const HALL_H = H - MARGIN * 2 - 80;
    const TECH_W = 50;

    floor.style.cssText = `width:${W}px;height:${H}px;position:relative;background:#0f1729;overflow:hidden;font-family:'Segoe UI',system-ui,sans-serif`;
    floor.innerHTML = '';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(W));
    svg.setAttribute('height', String(H));
    svg.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none';

    let paths = '';

    // subtle grid
    for (let i = 0; i <= 10; i++) {
        const x = HALL_X + (HALL_W / 10) * i;
        paths += `<line x1="${x}" y1="${HALL_Y}" x2="${x}" y2="${HALL_Y+HALL_H}" stroke="#1e293b" stroke-width="1" stroke-dasharray="4,6"/>`;
        paths += `<text x="${x}" y="${HALL_Y-6}" fill="#475569" font-size="10" text-anchor="middle">${i+1}</text>`;
    }
    for (let i = 0; i <= 3; i++) {
        const y = HALL_Y + (HALL_H / 3) * i;
        paths += `<line x1="${HALL_X}" y1="${y}" x2="${HALL_X+HALL_W}" y2="${y}" stroke="#1e293b" stroke-width="1" stroke-dasharray="4,6"/>`;
        paths += `<text x="${HALL_X-8}" y="${y+4}" fill="#475569" font-size="10" text-anchor="end">${String.fromCharCode(65+i)}</text>`;
    }

    // camera tehnica (left)
    paths += `<rect x="${HALL_X-TECH_W}" y="${HALL_Y}" width="${TECH_W}" height="${HALL_H}" fill="#0d2137" stroke="#1e4976" stroke-width="2" rx="2"/>`;
    paths += `<text x="${HALL_X-TECH_W/2}" y="${HALL_Y+HALL_H/2}" fill="#2d7ab8" font-size="9" text-anchor="middle" transform="rotate(-90,${HALL_X-TECH_W/2},${HALL_Y+HALL_H/2})">CAMERĂ TEHNICĂ (R1, R2)</text>`;

    // main hall
    paths += `<rect x="${HALL_X}" y="${HALL_Y}" width="${HALL_W}" height="${HALL_H}" fill="#111b2e" stroke="#334155" stroke-width="2.5" rx="2"/>`;

    // walls - thicker outer
    paths += `<rect x="${HALL_X}" y="${HALL_Y}" width="${HALL_W}" height="5" fill="#475569" rx="1"/>`;
    paths += `<rect x="${HALL_X}" y="${HALL_Y+HALL_H-5}" width="${HALL_W}" height="5" fill="#475569" rx="1"/>`;
    paths += `<rect x="${HALL_X}" y="${HALL_Y}" width="5" height="${HALL_H}" fill="#64748b" rx="1"/>`;
    paths += `<rect x="${HALL_X+HALL_W-5}" y="${HALL_Y}" width="5" height="${HALL_H}" fill="#64748b" rx="1"/>`;

    // exterior R3/R4 zone (right)
    const EXT_X = HALL_X + HALL_W + 15;
    const EXT_W = 55;
    paths += `<rect x="${EXT_X}" y="${HALL_Y+40}" width="${EXT_W}" height="${HALL_H-80}" fill="none" stroke="#06b6d4" stroke-width="1.5" stroke-dasharray="6,4" rx="4"/>`;
    paths += `<text x="${EXT_X+EXT_W/2}" y="${HALL_Y+HALL_H/2}" fill="#06b6d4" font-size="9" text-anchor="middle" transform="rotate(-90,${EXT_X+EXT_W/2},${HALL_Y+HALL_H/2})">EXTERIOR AER LIBER (R3, R4)</text>`;

    // structural columns
    for (let i = 0; i <= 10; i++) {
        for (let j = 0; j <= 3; j++) {
            const cx = HALL_X + (HALL_W / 10) * i;
            const cy = HALL_Y + (HALL_H / 3) * j;
            paths += `<rect x="${cx-4}" y="${cy-4}" width="8" height="8" fill="#334155" stroke="#475569" stroke-width="1" rx="1"/>`;
        }
    }

    // title block
    paths += `<text x="${W/2}" y="${H-12}" fill="#64748b" font-size="11" text-anchor="middle" font-weight="600">PLAN FABRICĂ — CAP PET PLAST S.R.L. — Sat Jucu de Mijloc, jud. Cluj — 61m × 14.4m</text>`;

    // north/south labels
    paths += `<text x="${HALL_X+HALL_W/2}" y="${HALL_Y-18}" fill="#94a3b8" font-size="10" text-anchor="middle" letter-spacing="2">PERETE NORD</text>`;
    paths += `<text x="${HALL_X+HALL_W/2}" y="${HALL_Y+HALL_H+22}" fill="#94a3b8" font-size="10" text-anchor="middle" letter-spacing="2">PERETE SUD</text>`;

    // scale indicator
    const scaleX = HALL_X + HALL_W - 100;
    const scaleY = H - 30;
    const scale6m = HALL_W / 10;
    paths += `<line x1="${scaleX}" y1="${scaleY}" x2="${scaleX+scale6m}" y2="${scaleY}" stroke="#94a3b8" stroke-width="2"/>`;
    paths += `<line x1="${scaleX}" y1="${scaleY-4}" x2="${scaleX}" y2="${scaleY+4}" stroke="#94a3b8" stroke-width="2"/>`;
    paths += `<line x1="${scaleX+scale6m}" y1="${scaleY-4}" x2="${scaleX+scale6m}" y2="${scaleY+4}" stroke="#94a3b8" stroke-width="2"/>`;
    paths += `<text x="${scaleX+scale6m/2}" y="${scaleY-6}" fill="#94a3b8" font-size="9" text-anchor="middle">6m</text>`;

    // compass
    paths += `<text x="${W-30}" y="${MARGIN+20}" fill="#64748b" font-size="16" text-anchor="middle">⊕</text>`;
    paths += `<text x="${W-30}" y="${MARGIN+32}" fill="#64748b" font-size="8" text-anchor="middle">N</text>`;

    svg.innerHTML = paths;
    floor.appendChild(svg);
}

function _layoutZoom(delta: number): void {
    _layoutState.zoom = Math.max(0.2, Math.min(5, _layoutState.zoom + delta));
    _layoutApplyTransform();
    const label = document.getElementById('layout-zoom-label') as HTMLElement | null;
    if (label) label.textContent = Math.round(_layoutState.zoom * 100) + '%';
}

function _layoutApplyTransform(): void {
    const canvas = document.getElementById('layout-canvas') as HTMLElement | null;
    if (canvas) canvas.style.transform = `translate(${_layoutState.offset.x}px, ${_layoutState.offset.y}px) scale(${_layoutState.zoom})`;
}

function _layoutRenderElements(): void {
    const container = document.getElementById('layout-elements') as HTMLElement | null;
    if (!container) return;
    container.innerHTML = '';
    const live = _layoutState.liveStatus || {};
    const edit = _layoutState.editMode;

    const sorted = _layoutState.equipment.map((eq, idx) => ({eq, idx})).sort((a,b) => (a.eq.z_index||0) - (b.eq.z_index||0));

    sorted.forEach(({eq, idx}) => {
        const isZone = eq.shape === 'zone';
        const isWall = eq.shape === 'wall';
        const isMachine = !isZone && !isWall;
        const s = isMachine ? (live[eq.machine_id] || {}) : {};
        const statusColor = s.status === 'stopped' ? '#ef4444' : s.status === 'warning' ? '#f59e0b' : '#22c55e';
        const statusGlow = s.status === 'stopped' ? '0 0 12px #ef444488' : s.status === 'warning' ? '0 0 12px #f59e0b88' : '0 0 8px #22c55e44';

        const el = document.createElement('div');
        el.className = `layout-el ${isZone ? 'layout-zone' : isWall ? 'layout-wall' : 'layout-eq'}`;
        el.dataset.type = 'equipment';
        el.dataset.mid = eq.machine_id;

        if (isZone) {
            el.style.cssText = `position:absolute;left:${eq.x_pct}%;top:${eq.y_pct}%;width:${eq.width_pct}%;height:${eq.height_pct}%;background:${eq.color}10;border:1px solid ${eq.color}30;border-radius:4px;cursor:${edit?'move':'default'};display:flex;align-items:flex-start;justify-content:flex-start;padding:6px 8px;z-index:${eq.z_index||0};transform:rotate(${eq.rotation||0}deg);transition:box-shadow .2s`;
            el.innerHTML = `<span style="font-size:10px;font-weight:700;color:${eq.color};opacity:.5;letter-spacing:.5px;text-transform:uppercase">${escHtml(eq.label)}</span>`;
        } else if (isWall) {
            el.style.cssText = `position:absolute;left:${eq.x_pct}%;top:${eq.y_pct}%;width:${eq.width_pct}%;height:${Math.max(eq.height_pct, 0.3)}%;background:${eq.color};border-radius:1px;cursor:${edit?'move':'default'};z-index:${eq.z_index||0};transform:rotate(${eq.rotation||0}deg);transition:box-shadow .2s;opacity:.6`;
        } else {
            const bgGrad = `linear-gradient(135deg, ${eq.color}33 0%, ${eq.color}11 100%)`;
            el.style.cssText = `position:absolute;left:${eq.x_pct}%;top:${eq.y_pct}%;width:${eq.width_pct}%;height:${eq.height_pct}%;background:${bgGrad};border:2px solid ${statusColor};border-radius:6px;cursor:${edit?'move':'pointer'};display:flex;align-items:center;justify-content:center;flex-direction:column;gap:1px;color:#e2e8f0;z-index:${eq.z_index||1};transform:rotate(${eq.rotation||0}deg);transition:all .2s;box-shadow:${statusGlow},inset 0 1px 0 rgba(255,255,255,.05);backdrop-filter:blur(2px)`;
            const shortLabel = (eq.label||'').replace(/^.*?-\s*/, '').substring(0, 24);
            el.innerHTML = `
                <div style="position:absolute;top:3px;right:4px;display:flex;align-items:center;gap:3px">
                    <span style="font-size:7px;color:${statusColor};text-transform:uppercase;font-weight:700">${s.status||'ok'}</span>
                    <div style="background:${statusColor};width:7px;height:7px;border-radius:50%;box-shadow:0 0 6px ${statusColor}"></div>
                </div>
                <strong style="font-size:11px;letter-spacing:.3px;text-shadow:0 1px 2px rgba(0,0,0,.5)">${escHtml(String(eq.machine_id))}</strong>
                <span style="font-size:8px;opacity:.7;max-width:95%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:center">${escHtml(shortLabel)}</span>`;
            el.addEventListener('mouseenter', () => { if(!edit) el.style.transform = `rotate(${eq.rotation||0}deg) scale(1.05)`; el.style.zIndex = '100'; });
            el.addEventListener('mouseleave', () => { el.style.transform = `rotate(${eq.rotation||0}deg) scale(1)`; el.style.zIndex = String(eq.z_index||1); });
        }

        el.addEventListener('click', e => {
            if (!edit && isMachine) { openMachineDetails(String(eq.machine_id)); return; }
            if (!edit) return;
            e.stopPropagation();
            document.querySelectorAll('.layout-el').forEach(x => x.classList.remove('layout-selected'));
            el.classList.add('layout-selected');
            _layoutState.selectedEl = { type: 'equipment', id: eq.machine_id, idx, data: eq };
            (document.getElementById('layout-delete-selected') as HTMLButtonElement).disabled = false;
            _layoutShowProps(eq, idx);
        });

        if (edit) {
            _layoutMakeDraggable(el, eq);
            _layoutMakeResizable(el, eq);
        }
        container.appendChild(el);
    });

    const showAnnots = !edit;
    _layoutState.annotations.forEach((ann, idx) => {
        if (ann.annotation_type === 'column' && !edit) return;
        if (ann.annotation_type === 'label' && !edit) return;
        const el = document.createElement('div');
        el.className = 'layout-el layout-annot';
        el.dataset.type = 'annotation';
        const isKey = ['door','panel','fire'].includes(ann.annotation_type);
        el.style.cssText = `position:absolute;left:${ann.x_pct}%;top:${ann.y_pct}%;transform:translate(-50%,-50%);cursor:${edit?'move':'default'};display:flex;align-items:center;gap:2px;font-size:9px;color:${ann.color};z-index:15;white-space:nowrap;opacity:${isKey?'1':'.7'}`;
        el.innerHTML = `<span style="font-size:14px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))">${ann.icon||'📌'}</span>${isKey?`<span style="text-shadow:0 1px 2px rgba(0,0,0,.8);font-weight:600">${escHtml(ann.label)}</span>`:''}`;

        el.addEventListener('click', e => {
            if (!edit) return;
            e.stopPropagation();
            document.querySelectorAll('.layout-el').forEach(x => x.classList.remove('layout-selected'));
            el.classList.add('layout-selected');
            _layoutState.selectedEl = { type: 'annotation', idx };
            (document.getElementById('layout-delete-selected') as HTMLButtonElement).disabled = false;
            _layoutShowPropsAnnot(ann, idx);
        });

        if (edit) _layoutMakeDraggable(el, ann, true);
        container.appendChild(el);
    });
}

function _layoutShowProps(eq: any, idx: number): void {
    const panel = document.getElementById('layout-props-panel') as HTMLElement | null;
    const title = document.getElementById('layout-props-title') as HTMLElement | null;
    const fields = document.getElementById('layout-props-fields') as HTMLElement | null;
    if (!panel || !fields || !title) return;
    panel.style.display = '';
    const isZone = eq.shape === 'zone' || eq.shape === 'wall';
    title.textContent = isZone ? `Zonă: ${eq.label}` : `Utilaj: ${eq.machine_id}`;
    fields.innerHTML = `
        <label style="font-size:.8rem">Label:<input class="form-control" style="width:120px;font-size:.8rem" value="${escHtml(eq.label||'')}" onchange="_layoutUpdateEq(${idx},'label',this.value)"></label>
        <label style="font-size:.8rem">Lăț%:<input type="number" class="form-control" style="width:60px;font-size:.8rem" value="${eq.width_pct}" step="0.5" onchange="_layoutUpdateEq(${idx},'width_pct',+this.value)"></label>
        <label style="font-size:.8rem">Înălț%:<input type="number" class="form-control" style="width:60px;font-size:.8rem" value="${eq.height_pct}" step="0.5" onchange="_layoutUpdateEq(${idx},'height_pct',+this.value)"></label>
        <label style="font-size:.8rem">Rotație°:<input type="number" class="form-control" style="width:60px;font-size:.8rem" value="${eq.rotation||0}" step="5" onchange="_layoutUpdateEq(${idx},'rotation',+this.value)"></label>
        <label style="font-size:.8rem">Culoare:<input type="color" style="width:35px;height:25px;border:none;cursor:pointer" value="${eq.color}" onchange="_layoutUpdateEq(${idx},'color',this.value)"></label>
        <label style="font-size:.8rem">Z:<input type="number" class="form-control" style="width:45px;font-size:.8rem" value="${eq.z_index||0}" onchange="_layoutUpdateEq(${idx},'z_index',+this.value)"></label>
    `;
}

function _layoutShowPropsAnnot(ann: any, idx: number): void {
    const panel = document.getElementById('layout-props-panel') as HTMLElement | null;
    const title = document.getElementById('layout-props-title') as HTMLElement | null;
    const fields = document.getElementById('layout-props-fields') as HTMLElement | null;
    if (!panel || !fields || !title) return;
    panel.style.display = '';
    title.textContent = `Punct: ${ann.label}`;
    fields.innerHTML = `
        <label style="font-size:.8rem">Label:<input class="form-control" style="width:120px;font-size:.8rem" value="${escHtml(ann.label||'')}" onchange="_layoutUpdateAnnot(${idx},'label',this.value)"></label>
        <label style="font-size:.8rem">Culoare:<input type="color" style="width:35px;height:25px;border:none;cursor:pointer" value="${ann.color}" onchange="_layoutUpdateAnnot(${idx},'color',this.value)"></label>
    `;
}

export function _layoutUpdateEq(idx: number, prop: string, val: any): void {
    if (_layoutState.equipment[idx]) { _layoutState.equipment[idx][prop] = val; _layoutRenderElements(); }
}
export function _layoutUpdateAnnot(idx: number, prop: string, val: any): void {
    if (_layoutState.annotations[idx]) { _layoutState.annotations[idx][prop] = val; _layoutRenderElements(); }
}

function _layoutMakeResizable(el: HTMLElement, data: any): void {
    const handle = document.createElement('div');
    handle.style.cssText = 'position:absolute;bottom:0;right:0;width:10px;height:10px;background:var(--accent);cursor:se-resize;border-radius:2px;opacity:.6';
    handle.addEventListener('mousedown', e => {
        e.stopPropagation(); e.preventDefault();
        const container = document.getElementById('layout-elements') as HTMLElement;
        const cRect = container.getBoundingClientRect();
        const zoom = _layoutState.zoom;
        const startW = data.width_pct, startH = data.height_pct;
        const startX = e.clientX, startY = e.clientY;
        const onMove = (ev: MouseEvent) => {
            data.width_pct = Math.max(1, startW + (ev.clientX - startX) / (cRect.width * zoom) * 100);
            data.height_pct = Math.max(0.3, startH + (ev.clientY - startY) / (cRect.height * zoom) * 100);
            el.style.width = data.width_pct + '%';
            el.style.height = data.height_pct + '%';
        };
        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    });
    el.appendChild(handle);
}

function _layoutMakeDraggable(el: HTMLElement, data: any, isAnnotation = false): void {
    let startX: number, startY: number, origX: number, origY: number;
    el.addEventListener('mousedown', e => {
        if (!_layoutState.editMode) return;
        e.stopPropagation();
        e.preventDefault();
        startX = e.clientX; startY = e.clientY;
        origX = data.x_pct; origY = data.y_pct;
        const container = document.getElementById('layout-elements') as HTMLElement;
        const cRect = container.getBoundingClientRect();
        const zoom = _layoutState.zoom;

        const onMove = (ev: MouseEvent) => {
            const dx = (ev.clientX - startX) / (cRect.width * zoom) * 100;
            const dy = (ev.clientY - startY) / (cRect.height * zoom) * 100;
            data.x_pct = Math.max(0, Math.min(100, origX + dx));
            data.y_pct = Math.max(0, Math.min(100, origY + dy));
            el.style.left = data.x_pct + '%';
            el.style.top = data.y_pct + '%';
        };
        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    });
}

export function _layoutViewPhoto(url: string): void {
    const viewer = document.getElementById('layout-photo-viewer') as HTMLElement | null;
    const img = document.getElementById('layout-photo-viewer-img') as HTMLImageElement | null;
    if (viewer && img) {
        img.src = url;
        viewer.classList.remove('hidden');
        viewer.style.display = 'flex';
        viewer.onclick = e => { if (e.target !== img) { viewer.classList.add('hidden'); viewer.style.display = 'none'; } };
    }
}

// Delete a photo from the factory hall gallery.
// Fix 2026-05-14: function was previously module-local so the inline
// `onclick="_layoutDeletePhoto(id)"` in the gallery thumbnails resolved to
// `undefined` once the bundle ran inside its IIFE → click did nothing.
// Now exported and wired to `window` in main.ts so inline handlers can find it.
export async function _layoutDeletePhoto(photoId: number): Promise<void> {
    if (!await askConfirm('Șterge poza definitiv? Acțiunea nu se poate anula.')) return;
    try {
        const res = await fetch(`/api/layout/photos/${photoId}`, { method: 'DELETE', credentials: 'include' });
        if (!res.ok) {
            let detail = '';
            try { const j = await res.json(); detail = j?.error || ''; } catch (_) { /* ignore */ }
            if (res.status === 401 || res.status === 403) {
                toast(`Nu ai dreptul să ștergi poza (necesar rol master).${detail ? ' ' + detail : ''}`, 'error');
            } else {
                toast(`Eroare ${res.status} la ștergere${detail ? ': ' + detail : ''}`, 'error');
            }
            return;
        }
        // Optimistic UI: remove the thumbnail immediately + decrement counter.
        const btn = document.querySelector(`button[onclick*="_layoutDeletePhoto(${photoId})"]`) as HTMLElement | null;
        const thumb = btn?.closest('.layout-photo-thumb') as HTMLElement | null;
        thumb?.remove();
        _layoutState.photos = (_layoutState.photos || []).filter((p: any) => p.id !== photoId);
        const headerCount = document.getElementById('layout-photos-count');
        if (headerCount) headerCount.textContent = `(${_layoutState.photos.length} poze)`;
        toast('Poză ștearsă');
    } catch (err) {
        console.error('_layoutDeletePhoto error:', err);
        toast('Eroare reţea la ștergere poză', 'error');
    }
}
